import type { CanvasNode } from "./types"
import { getWasmExports, type WasmExports } from "./wasmRuntime"

const LABEL_CHAR_WIDTH = 6
const LABEL_MAX_WIDTH = 120

export class Scene {
  count = 0
  ids: string[] = []
  names: string[] = []
  colors: string[] = []
  version = 0

  private indexById = new Map<string, number>()
  private centers = new Float64Array(0)
  private widths = new Float64Array(0)
  private heights = new Float64Array(0)
  private syncedVersion = -1
  private memoryBuffer: ArrayBuffer | SharedArrayBuffer | null = null
  private positionsPtr = 0
  private labelWidthsPtr = 0
  private positionsView: Float32Array | null = null
  private labelWidthsView: Uint16Array | null = null

  indexOf(id: string): number | undefined {
    return this.indexById.get(id)
  }

  setData(nodes: CanvasNode[]) {
    this.count = nodes.length
    this.ids = new Array(nodes.length)
    this.names = new Array(nodes.length)
    this.colors = new Array(nodes.length)
    this.indexById.clear()
    if (this.centers.length !== nodes.length * 2) {
      this.centers = new Float64Array(nodes.length * 2)
    }
    if (this.widths.length !== nodes.length) {
      this.widths = new Float64Array(nodes.length)
      this.heights = new Float64Array(nodes.length)
    }

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i]
      this.ids[i] = node.id
      this.names[i] = node.name
      this.colors[i] = node.color
      this.indexById.set(node.id, i)
      this.centers[i * 2] = node.x + node.width / 2
      this.centers[i * 2 + 1] = node.y + node.height / 2
      this.widths[i] = node.width
      this.heights[i] = node.height
    }

    this.positionsView = null
    this.labelWidthsView = null
    this.syncedVersion = -1
    this.version += 1
  }

  sync(): boolean {
    const exports = getWasmExports()
    if (!exports) return false
    if (this.syncedVersion === this.version) return true

    exports.scene_alloc(this.count)
    this.positionsPtr = exports.scene_positions()
    this.labelWidthsPtr = exports.scene_label_widths()
    this.memoryBuffer = null
    const positions = this.positionsViewFor(exports)
    const labelWidths = this.labelWidthsViewFor(exports)
    if (!positions || !labelWidths) return false

    for (let i = 0; i < this.count * 2; i++) {
      positions[i] = this.centers[i]
    }
    for (let i = 0; i < this.count; i++) {
      labelWidths[i] = approxLabelWidth(this.names[i])
    }

    this.syncedVersion = this.version
    return true
  }

  positions(): Float32Array | null {
    const exports = getWasmExports()
    if (!exports || !this.sync()) return null
    return this.positionsViewFor(exports)
  }

  centerX(index: number): number {
    return this.centers[index * 2]
  }

  centerY(index: number): number {
    return this.centers[index * 2 + 1]
  }

  width(index: number): number {
    return this.widths[index]
  }

  height(index: number): number {
    return this.heights[index]
  }

  minX(index: number): number {
    return this.centerX(index) - this.width(index) / 2
  }

  minY(index: number): number {
    return this.centerY(index) - this.height(index) / 2
  }

  maxX(index: number): number {
    return this.centerX(index) + this.width(index) / 2
  }

  maxY(index: number): number {
    return this.centerY(index) + this.height(index) / 2
  }

  free() {
    const exports = getWasmExports()
    exports?.scene_free()
    this.positionsView = null
    this.labelWidthsView = null
    this.memoryBuffer = null
    this.positionsPtr = 0
    this.labelWidthsPtr = 0
    this.syncedVersion = -1
  }

  private positionsViewFor(exports: WasmExports): Float32Array | null {
    this.refreshViewsIfNeeded(exports)
    return this.positionsView
  }

  private labelWidthsViewFor(exports: WasmExports): Uint16Array | null {
    this.refreshViewsIfNeeded(exports)
    return this.labelWidthsView
  }

  private refreshViewsIfNeeded(exports: WasmExports) {
    // Wasm memory growth detaches previous views; re-create them on buffer swap.
    if (this.memoryBuffer === exports.memory.buffer) return
    this.memoryBuffer = exports.memory.buffer
    this.positionsView = new Float32Array(
      exports.memory.buffer,
      this.positionsPtr,
      this.count * 2,
    )
    this.labelWidthsView = new Uint16Array(
      exports.memory.buffer,
      this.labelWidthsPtr,
      this.count,
    )
  }
}

export function approxLabelWidth(name: string) {
  return Math.min(name.length * LABEL_CHAR_WIDTH, LABEL_MAX_WIDTH) + 4
}
